import { useState } from "react";
import { trpc } from "../lib/trpc";
import { useTranslation } from "../i18n";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Switch } from "../components/ui/switch";
import { Trash2, Plus, Save } from "lucide-react";
import { toast } from "sonner";
import { DEFAULT_CURRENCY, type SupportedCurrency } from "@shared/const";

function getCurrencySymbol(currency: SupportedCurrency): string {
  const symbols: Record<SupportedCurrency, string> = {
    EUR: "€",
    USD: "$",
  };
  return symbols[currency] || "€";
}

export function ShippingRatesManager({ currency = DEFAULT_CURRENCY }: { currency?: SupportedCurrency }) {
  const { t } = useTranslation();
  const currencySymbol = getCurrencySymbol(currency);
  const ts = {
    loading: t?.common?.loading || "Laden...",
    title: t?.settings?.shippingRatesTitle || "Versandkosten nach Land",
    description:
      t?.settings?.shippingRatesDescription ||
      "Konfigurieren Sie die Versandkosten für verschiedene Länder. Nur aktive Länder werden beim Checkout als Lieferadresse angeboten.",
    country: t?.settings?.shippingRatesCountry || "Land",
    cost: t?.settings?.shippingRatesCost || "Kosten",
    active: t?.settings?.shippingRatesActive || "Aktiv",
    actions: t?.settings?.shippingRatesActions || "Aktionen",
    edit: t?.common?.edit || "Bearbeiten",
    delete: t?.common?.delete || "Löschen",
    addCountryButton: t?.settings?.shippingRatesAddButton || "Neues Land hinzufügen",
    addCountryTitle: t?.settings?.shippingRatesAddTitle || "Neues Land hinzufügen",
    selectCountryPlaceholder: t?.settings?.shippingRatesSelectPlaceholder || "Land wählen...",
    customCountryPlaceholder: t?.settings?.shippingRatesCustomPlaceholder || "Ländername eingeben",
    customCountryOption: t?.settings?.shippingRatesCustomOption || "Anderes Land...",
    costLabel: `${t?.settings?.shippingRatesCostInputLabel || "Versandkosten"} (${currencySymbol})`,
    costPlaceholder: t?.settings?.shippingRatesCostInputPlaceholder || "0.00",
    noRates: t?.settings?.shippingRatesEmpty || "Noch keine Versandkosten konfiguriert",
    toastSaved: t?.settings?.shippingRatesToastSaved || "Versandkosten gespeichert",
    toastDeleted: t?.settings?.shippingRatesToastDeleted || "Versandkosten gelöscht",
    toastErrorPrefix: t?.settings?.shippingRatesToastErrorPrefix || "Fehler:",
    formMissingFields: t?.settings?.shippingRatesFormMissing || "Bitte Land und Kosten eingeben",
    deleteConfirm: t?.settings?.shippingRatesDeleteConfirm || "Versandkosten wirklich löschen?",
    save: t?.common?.save || "Speichern",
    cancel: t?.common?.cancel || "Abbrechen",
  };
  const countryOptions =
    (t?.settings?.shippingRatesCountryOptions as string[] | undefined) || [
      "Deutschland",
      "Österreich",
      "Schweiz",
      "Bosnien und Herzegowina",
      "Kroatien",
      "Serbien",
      "Montenegro",
      "Slowenien",
      "Nordmazedonien",
    ];
  const [editingId, setEditingId] = useState<number | null>(null);
  const [newCountry, setNewCountry] = useState("");
  const [isCustomCountry, setIsCustomCountry] = useState(false);
  const [newCost, setNewCost] = useState("");
  const [showAddForm, setShowAddForm] = useState(false);

  const utils = trpc.useUtils();
  const { data: rates, isLoading } = trpc.settings.getShippingRates.useQuery();
  
  const upsertMutation = trpc.settings.upsertShippingRate.useMutation({
    onSuccess: () => {
      toast.success(ts.toastSaved);
      utils.settings.getShippingRates.invalidate();
      setEditingId(null);
      setShowAddForm(false);
      setNewCountry("");
      setNewCost("");
      setIsCustomCountry(false);
    },
    onError: (error) => {
      toast.error(`${ts.toastErrorPrefix} ${error.message}`);
    },
  });

  const deleteMutation = trpc.settings.deleteShippingRate.useMutation({
    onSuccess: () => {
      toast.success(ts.toastDeleted);
      utils.settings.getShippingRates.invalidate();
    },
    onError: (error) => {
      toast.error(`${ts.toastErrorPrefix} ${error.message}`);
    },
  });

  const handleAdd = () => {
    if (!newCountry || !newCost) {
      toast.error(ts.formMissingFields);
      return;
    }

    upsertMutation.mutate({
      country: newCountry,
      cost: newCost,
      isActive: true,
    });
  };

  const handleUpdate = (id: number, country: string, cost: string, isActive: boolean) => {
    upsertMutation.mutate({
      id,
      country,
      cost,
      isActive,
    });
  };

  const handleDelete = (id: number) => {
    if (confirm(ts.deleteConfirm)) {
      deleteMutation.mutate({ id });
    }
  };

  if (isLoading) {
    return <div>{ts.loading}</div>;
  }

  return (
    <Card className="backdrop-blur-sm bg-white/80 dark:bg-gray-800/80 border-purple-200 dark:border-purple-800 shadow-xl hover:shadow-2xl transition-all duration-300">
      <CardHeader>
        <CardTitle>{ts.title}</CardTitle>
        <CardDescription>{ts.description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{ts.country}</TableHead>
                <TableHead>{ts.cost}</TableHead>
                <TableHead>{ts.active}</TableHead>
                <TableHead className="text-right">{ts.actions}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rates && rates.length > 0 ? (
                rates.map((rate) => (
                  <TableRow key={rate.id}>
                    <TableCell className="font-medium">{rate.country}</TableCell>
                    <TableCell>
                      {editingId === rate.id ? (
                        <Input
                          type="number"
                          step="0.01"
                          defaultValue={rate.cost}
                          className="w-24"
                          id={`cost-${rate.id}`}
                        />
                      ) : (
                        `${parseFloat(rate.cost).toFixed(2)} ${currencySymbol}`
                      )}
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={rate.isActive}
                        onCheckedChange={(checked) => {
                          handleUpdate(
                            rate.id,
                            rate.country,
                            rate.cost,
                            checked
                          );
                        }}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        {editingId === rate.id ? (
                          <Button
                            size="sm"
                            onClick={() => {
                              const costInput = document.getElementById(
                                `cost-${rate.id}`
                              ) as HTMLInputElement;
                              handleUpdate(
                                rate.id,
                                rate.country,
                                costInput.value,
                                rate.isActive
                              );
                            }}
                          >
                            <Save className="h-4 w-4" />
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setEditingId(rate.id)}
                          >
                            {ts.edit}
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleDelete(rate.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-muted-foreground">
                    {ts.noRates}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        {!showAddForm ? (
          <Button onClick={() => setShowAddForm(true)}>
            <Plus className="h-4 w-4 mr-2" />
            {ts.addCountryButton}
          </Button>
        ) : (
          <div className="space-y-4 border rounded-lg p-4 bg-muted/50">
            <h4 className="font-medium">{ts.addCountryTitle}</h4>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="newCountry">{ts.country}</Label>
                <select
                  id="newCountry"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  value={isCustomCountry ? "__custom__" : newCountry}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === "__custom__") {
                      setIsCustomCountry(true);
                      setNewCountry("");
                    } else {
                      setIsCustomCountry(false);
                      setNewCountry(value);
                    }
                  }}
                >
                  <option value="">{ts.selectCountryPlaceholder}</option>
                  {countryOptions.map((country) => (
                    <option key={country} value={country}>
                      {country}
                    </option>
                  ))}
                  <option value="__custom__">{ts.customCountryOption}</option>
                </select>
                {isCustomCountry && (
                  <Input
                    placeholder={ts.customCountryPlaceholder}
                    value={newCountry}
                    onChange={(e) => setNewCountry(e.target.value)}
                  />
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="newCost">{ts.costLabel}</Label>
                <Input
                  id="newCost"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder={ts.costPlaceholder}
                  value={newCost}
                  onChange={(e) => setNewCost(e.target.value)}
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={handleAdd}
                disabled={upsertMutation.isPending || !newCountry || !newCost}
              >
                <Save className="h-4 w-4 mr-2" />
                {ts.save}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowAddForm(false);
                  setNewCountry("");
                  setNewCost("");
                  setIsCustomCountry(false);
                }}
              >
                {ts.cancel}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
